<template>
  <div class="common-upload">
    <el-upload
      class="upload-demo t-left"
      action="#"
      :auto-upload="true"
      :http-request="doUpload"
      :show-file-list="false"
      :accept="accept"
      :multiple="multiple"
      :disabled="viewMode"
    >
      <div v-if="!viewMode" class="import-btn-tips">
        <el-button size="mini" icon="el-icon-my-import" type="importNew">上传文件</el-button>
        <div class="import-step-tip">{{ tip }}{{ maxCount > 0 ? `，最多支持${maxCount}个文件` : "" }}，单个文件大小≤{{ maxSize }}M</div>
      </div>
    </el-upload>
    <!-- 自渲染附件列表：绕开 el-upload 内置 file-list 的 uid/位移动画/滚动条行为，
         数据唯一来源是 v-model 的 value，删除/查看/下载全由本组件控制 -->
    <ul v-if="value && value.length" class="file-list">
      <li v-for="(f, idx) in value" :key="f.filePath || idx" class="file-item">
        <span class="file-name" :title="f.fileName">{{ f.fileName }}</span>
        <span v-if="!viewMode" class="file-action" @click="removeFile(f)">删除</span>
        <span class="file-action" @click="onPreview(f)">查看</span>
        <span class="file-action" @click="onDownload(f)">下载</span>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * 附件上传通用组件（obs 直传 + 项目通用样式）。
 *
 * v-model 结构：[{ fileName, filePath }]，filePath 为 obs 返回的完整地址；
 * 编辑回显直接把后端明细数组绑上即可。
 *
 * 样式约定：触发区依赖全局 style.css 的 .import-btn-tips/.import-step-tip 与
 * type="importNew" 按钮；文件行为本组件 scoped（长文件名单行省略+悬浮全名，
 * 操作链接恒横排）。查看/下载规则同 upstreamOperationReportAdd 运营报告附件。
 */
export default {
  name: "CommonUpload",
  props: {
    /** 文件明细 [{fileName, filePath}] */
    value: { type: Array, default: () => [] },
    /** 只读（隐藏导入与删除，保留查看/下载） */
    viewMode: { type: Boolean, default: false },
    multiple: { type: Boolean, default: true },
    /** el-upload accept，如 ".docx,.pdf" */
    accept: { type: String, default: "" },
    /** 单文件大小上限 MB */
    maxSize: { type: Number, default: 20 },
    /** 附件数量上限（0=不限，默认 20 个） */
    maxCount: { type: Number, default: 20 },
    /** 触发区提示文案（大小与数量上限自动拼接） */
    tip: { type: String, default: "支持多个文件" },
  },
  methods: {
    doUpload(content) {
      if (this.maxCount > 0 && (this.value || []).length >= this.maxCount) {
        // 同一批多选超限只弹一次：本轮已弹过则静默丢弃；宏任务延迟复位，整批内标志位存活
        if (!this._maxCountNotified) {
          this.$message.error(`最多支持${this.maxCount}个文件`);
          this._maxCountNotified = true;
          setTimeout(() => {
            this._maxCountNotified = false;
          }, 500);
        }
        return;
      }
      if (content.file.size / 1024 / 1024 >= this.maxSize) {
        this.$message.error(`上传文件大小不能超过${this.maxSize}MB`);
        return;
      }
      const formData = new FormData();
      formData.append("file", content.file);
      formData.append("fileName", content.file.name);
      // 在途计数：多选并行上传时，只有最后一个响应回来才弹一次"上传成功"
      this._pending = (this._pending || 0) + 1;
      this.api
        .postImage(
          `/${serverConfig.msCommonAdminCloudService}/obs/upload-file-include-filename`,
          formData
        )
        .then((res) => {
          this._pending = Math.max(0, (this._pending || 0) - 1);
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE) {
            this.$emit("input", [
              ...(this.value || []),
              { fileName: content.file.name, filePath: res.data.data.data },
            ]);
            if (this._pending === 0) {
              this.$message.success("上传成功");
            }
          } else {
            this.$message.error(res.data.msg || "上传失败");
          }
        });
    },
    removeFile(file) {
      this.$emit("input", (this.value || []).filter((f) => f.filePath !== file.filePath));
    },
    // 图片/pdf/压缩包新窗口直开，office 系走在线预览
    onPreview(file) {
      const name = file.fileName || "";
      const suffix = name.substring(name.lastIndexOf(".") + 1);
      if (["png", "jpeg", "jpg", "pdf", "zip", "rar"].includes(suffix)) {
        return window.open(file.filePath);
      }
      window.open("https://view.officeapps.live.com/op/view.aspx?src=" + file.filePath);
    },
    onDownload(file) {
      window.open(file.filePath + "?response-content-type=application/octet-stream");
    },
  },
};
</script>
<style scoped>
/* 2026-10-08 样式反馈：触发区改纵向堆叠——上传按钮与"文件上传"label 垂直居中（按钮留在 form-item
     首行 40px 行高内自然居中），提示文案换行展示在按钮下方并左对齐（原全局 flex 横排把文案挤在
     按钮右侧窄区换行）。.import-btn-tips 全局样式被 10+ 老页面引用，仅在本组件内覆盖 */
.common-upload /deep/ .import-btn-tips {
  display: block;
  /* 2026-10-10 样式反馈：上传按钮左移贴齐表单标签——Element .el-upload（theme-chalk）自带
     text-align:center，10-08 改纵向堆叠（display:block）后暴露，按钮被推到内容列中间；
     此处对触发区整体补左对齐（提示文案已单独设 left） */
  text-align: left;
}
.common-upload /deep/ .import-btn-tips .import-step-tip {
  display: block;
  margin: 4px 0 0;
  text-align: left;
  line-height: 18px;
  white-space: normal;
}
/* 自渲染附件列表：限高滚动（约 7 行超高后内部滚动），布局/动画完全自控 */
.file-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 210px;
  overflow-y: auto;
  text-align: left;
}
.file-item {
  display: flex;
  align-items: center;
  line-height: 26px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}
.file-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.file-action {
  margin-left: 10px;
  cursor: pointer;
  text-decoration: underline;
  color: #4fa1a4;
  flex-shrink: 0;
}
</style>
